import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Mail, CheckCircle, AlertCircle, User, Phone } from 'lucide-react';
import { apiService } from '../services/api';
import { getApiErrorMessage } from '../lib/apiErrors';
import type { CustomerPortalRegistrationRequest } from '../types/auth';

interface MagicLinkAuthProps {
  organizationId: string;
  redirectUrl?: string;
  onSuccess?: () => void;
}

type FlowState = 'email' | 'registration' | 'success';

interface RegistrationData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

export const MagicLinkAuth: React.FC<MagicLinkAuthProps> = ({ 
  organizationId,
  redirectUrl,
  onSuccess 
}) => {
  const [flowState, setFlowState] = useState<FlowState>('email');
  const [email, setEmail] = useState('');
  const [registrationData, setRegistrationData] = useState<RegistrationData>({
    firstName: '',
    lastName: '',
    phoneNumber: ''
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [customerName, setCustomerName] = useState('');

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Please enter your email address.' });
      return;
    }

    if (!organizationId) {
      setMessage({ type: 'error', text: 'Organization not specified.' });
      return;
    }

    setMessage(null);
    setSubmitting(true);

    try {
      const response = await apiService.requestMagicLink({
        email: email.trim(),
        organizationId,
        redirectUrl
      });

      if (response.success) {
        // Customer exists, magic link sent
        setFlowState('success');
        setMessage({ 
          type: 'success', 
          text: 'Magic link sent! Please check your email and click the link to access your tickets.' 
        });
        onSuccess?.();
      } else {
        // Check if it's a registration required error
        if (response.errors && response.errors.includes('CUSTOMER_REGISTRATION_REQUIRED')) {
          setFlowState('registration');
          setMessage({
            type: 'info',
            text: 'Please complete your registration to access this organization\'s customer portal.'
          });
        } else if (response.errors && response.errors.includes('CUSTOMER_SELF_REGISTRATION_DISABLED')) {
          // Self-registration is disabled for this organization
          setMessage({
            type: 'error',
            text: 'No account with this email found. Please contact support if you need an account.'
          });
        } else {
          setMessage({
            type: 'error',
            text: getApiErrorMessage(response, 'Failed to send magic link. Please try again.')
          });
        }
      }
    } catch (error) {
      console.error('Magic link request error:', error);
      setMessage({ 
        type: 'error', 
        text: 'An error occurred. Please try again later.' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegistrationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!registrationData.firstName.trim() || !registrationData.lastName.trim()) {
      setMessage({ type: 'error', text: 'First name and last name are required.' });
      return;
    }

    setMessage(null);
    setSubmitting(true);

    try {
      const registrationRequest: CustomerPortalRegistrationRequest = {
        firstName: registrationData.firstName.trim(),
        lastName: registrationData.lastName.trim(),
        email: email.trim(),
        phoneNumber: registrationData.phoneNumber.trim() || undefined,
        organizationId,
        redirectUrl
      };

      const response = await apiService.registerCustomerAndRequestMagicLink(registrationRequest);

      if (response.success) {
        setCustomerName(response.data?.customerName || '');
        setFlowState('success');
        setMessage({
          type: 'success',
          text: 'Registration successful! Check your email for a login link.'
        });
        onSuccess?.();
      } else {
        // Check if self-registration is disabled
        if (response.errors && response.errors.includes('CUSTOMER_SELF_REGISTRATION_DISABLED')) {
          setMessage({
            type: 'error',
            text: 'No account with this email found. Please contact support if you need an account.'
          });
        } else {
          setMessage({
            type: 'error',
            text: getApiErrorMessage(response, 'Registration failed. Please try again.')
          });
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      setMessage({ 
        type: 'error', 
        text: 'An error occurred during registration. Please try again later.' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleTryAgain = () => {
    setFlowState('email');
    setMessage(null);
    setEmail('');
    setRegistrationData({
      firstName: '',
      lastName: '',
      phoneNumber: ''
    });
  };

  const handleBackToEmail = () => {
    setFlowState('email');
    setMessage(null);
  };

  // Success state
  if (flowState === 'success') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center px-4 sm:px-6 pt-4 sm:pt-6 pb-2">
          <div className="mx-auto mb-3 sm:mb-4 h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
          </div>
          <CardTitle className="text-lg sm:text-xl">Check Your Email</CardTitle>
          <CardDescription className="text-sm sm:text-base">
            We've sent a secure login link to <strong>{email}</strong>
            {customerName && <><br />Welcome, {customerName}!</>}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 px-4 sm:px-6 pt-2 pb-4 sm:pb-6">
          {message && (
            <Alert className={message.type === 'error' ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}>
              {message.type === 'error' ? (
                <AlertCircle className="h-4 w-4 text-red-600" />
              ) : (
                <CheckCircle className="h-4 w-4 text-green-600" />
              )}
              <AlertDescription className={message.type === 'error' ? 'text-red-800' : 'text-green-800'}>
                {message.text}
              </AlertDescription>
            </Alert>
          )}
          
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 space-y-2">
            <p>• Click the link in your email to access your support tickets</p>
            <p>• The link will expire in 30 minutes for security</p>
            <p>• Check your spam folder if you don't see the email</p>
          </div>

          <div className="flex flex-col gap-2">
            <Button 
              variant="outline" 
              onClick={handleTryAgain}
              className="w-full"
            >
              Try Different Email
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Registration state
  if (flowState === 'registration') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center px-4 sm:px-6 pt-4 sm:pt-6 pb-2">
          <div className="mx-auto mb-3 sm:mb-4 h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-blue-100 flex items-center justify-center">
            <User className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
          </div>
          <CardTitle className="text-lg sm:text-xl">Complete Registration</CardTitle>
          <CardDescription className="text-sm sm:text-base">
            Please provide your details to create your account for <strong>{email}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 sm:px-6 pt-2 pb-4 sm:pb-6">
          <form onSubmit={handleRegistrationSubmit} className="space-y-4">
            {message && (
              <Alert className={
                message.type === 'error' ? 'border-red-200 bg-red-50' : 
                message.type === 'success' ? 'border-green-200 bg-green-50' : 
                'border-blue-200 bg-blue-50'
              }>
                {message.type === 'error' ? (
                  <AlertCircle className="h-4 w-4 text-red-600" />
                ) : message.type === 'success' ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <User className="h-4 w-4 text-blue-600" />
                )}
                <AlertDescription className={
                  message.type === 'error' ? 'text-red-800' : 
                  message.type === 'success' ? 'text-green-800' : 
                  'text-blue-800'
                }>
                  {message.text}
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name <span className="text-red-500">*</span></Label>
                <Input
                  id="firstName"
                  placeholder="John"
                  value={registrationData.firstName}
                  onChange={(e) => setRegistrationData(prev => ({ ...prev, firstName: e.target.value }))}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name <span className="text-red-500">*</span></Label>
                <Input
                  id="lastName"
                  placeholder="Doe"
                  value={registrationData.lastName}
                  onChange={(e) => setRegistrationData(prev => ({ ...prev, lastName: e.target.value }))}
                  required
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="Your phone number"
                  className="pl-10"
                  value={registrationData.phoneNumber}
                  onChange={(e) => setRegistrationData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2 pt-2">
              <Button 
                type="submit" 
                className="w-full" 
                disabled={submitting || !registrationData.firstName.trim() || !registrationData.lastName.trim()}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    <User className="mr-2 h-4 w-4" />
                    Complete Registration & Send Login Link
                  </>
                )}
              </Button>
              
              <Button 
                type="button" 
                variant="ghost" 
                onClick={handleBackToEmail}
                className="w-full"
                disabled={submitting}
              >
                Back to Email Entry
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center px-4 sm:px-6 pt-4 sm:pt-6 pb-2">
        <div className="mx-auto mb-3 sm:mb-4 h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-blue-100 flex items-center justify-center">
          <Mail className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
        </div>
        <CardTitle className="text-lg sm:text-xl">Access Your Tickets</CardTitle>
        <CardDescription className="text-sm sm:text-base">
          Enter your email to receive a secure login link
        </CardDescription>
      </CardHeader>
      <CardContent className="px-4 sm:px-6 pt-2 pb-4 sm:pb-6">
        <form onSubmit={handleEmailSubmit} className="space-y-4">
          {message && (
            <Alert className={message.type === 'error' ? 'border-red-200 bg-red-50' : message.type === 'success' ? 'border-green-200 bg-green-50' : 'border-blue-200 bg-blue-50'}>
              {message.type === 'error' ? (
                <AlertCircle className="h-4 w-4 text-red-600" />
              ) : message.type === 'success' ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <Mail className="h-4 w-4 text-blue-600" />
              )}
              <AlertDescription className={
                message.type === 'error' ? 'text-red-800' : 
                message.type === 'success' ? 'text-green-800' : 
                'text-blue-800'
              }>
                {message.text}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="your.email@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={submitting}
              className="w-full"
              autoComplete="email"
              autoFocus
            />
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={submitting || !email.trim()}
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Checking Account...
              </>
            ) : (
              <>
                <Mail className="mr-2 h-4 w-4" />
                Send Magic Link
              </>
            )}
          </Button>

          <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-center space-y-1">
            <p>We'll send you a secure link that expires in 30 minutes.</p>
            <p>Don't have an account? We'll help you register after verifying your email.</p>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default MagicLinkAuth;